import { useCallback } from 'react';

import { useOrg } from '@/contexts/OrgContext';
import { useFeatureFlag } from '@/hooks/useFeatureFlag';
import { usePermissions } from '@/hooks/usePermissions';
import { CARD_BORDER } from '@/lib/base/list-card';
import { isApiError } from '@/lib/errors';
import {
  VendorImportCard,
  type VendorImportWizardHandoff,
} from '@/lib/vendor-import';

import { useHubspotStatus } from '../../hooks/useHubspotStatus';
import { HubspotIcon } from '../../HubspotIcon';
import { HubspotOauthErrorBanner } from '../HubspotOauthErrorBanner';
import { HubspotImportWizard } from './HubspotImportWizard';

function HubspotImportUnavailable() {
  return (
    <div className={`${CARD_BORDER} flex items-center gap-3 px-4 py-3`}>
      <HubspotIcon className='size-5 shrink-0 text-fg-tertiary' />
      <p className='text-sm text-fg-tertiary'>
        HubSpot import is managed by your workspace admins. Ask an admin to
        enable it.
      </p>
    </div>
  );
}

export function HubspotImportCard() {
  const enabled = useFeatureFlag('hubspot_import');
  const { currentOrg } = useOrg();
  const orgId = currentOrg?.id;
  const { hasPermissionGroup } = usePermissions();
  const isAdmin = hasPermissionGroup('ahoy_other_admin');
  // Only fetch status when the feature is enabled AND the user is an admin —
  // the HubSpot status endpoint is admin-only, so a non-admin member would
  // otherwise fire a doomed 403 request (retried by React Query) before the
  // `!isAdmin` early return below. Passing `undefined` keeps the query disabled.
  const status = useHubspotStatus(enabled && isAdmin ? orgId : undefined);

  const renderWizard = useCallback(
    (h: VendorImportWizardHandoff) => (
      <HubspotImportWizard
        orgId={h.orgId}
        isOpen={h.isOpen}
        onClose={h.onClose}
        onImportCreated={h.onImportCreated}
      />
    ),
    []
  );

  if (!enabled) {
    return null;
  }

  if (!isAdmin) {
    return <HubspotImportUnavailable />;
  }

  const forbidden =
    status.isError && isApiError(status.error) && status.error.isForbidden();

  if (forbidden) {
    return <HubspotImportUnavailable />;
  }

  // The OAuth callback redirects import-intent users back here
  // (return_to=settings-import) with `?hubspot_error=…` on failure/expiry, so
  // the banner must be mounted on this page too — not only on /settings/hubspot.
  return (
    <div className='space-y-4'>
      <HubspotOauthErrorBanner />
      <VendorImportCard
        orgId={orgId}
        title='Import from HubSpot'
        description='Bring your HubSpot contacts, companies, and deals into Ahoy.'
        ctaLabel='Import from HubSpot'
        icon={<HubspotIcon className='size-5 text-fg-primary' />}
        renderWizard={renderWizard}
      />
    </div>
  );
}
